import { damp, dampAngle, TAU } from '../core/math';
import { fbm1, noise1 } from '../core/noise';

const Z0 = 0.0002; // sea surface roughness length, m
const LOG_REF = Math.log(10 / Z0);

/**
 * True wind. `fromBearing` follows meteorological convention (the compass
 * direction the wind comes FROM); `dirX/dirZ` is the unit vector it blows toward.
 */
export class Wind {
  meanSpeed = 6;
  meanFrom = 0;
  gustiness = 0.2;

  speed = 6;
  fromBearing = 0;
  dirX = 0;
  dirZ = 1;
  /** Short-term gust factor, ~1 +- gustiness. */
  gust = 1;
  private t = 0;

  constructor(from: number, speed: number) {
    this.meanFrom = from;
    this.fromBearing = from;
    this.meanSpeed = speed;
    this.speed = speed;
    this.updateVector();
  }

  update(dt: number, targetSpeed: number, targetFrom: number, gustiness: number): void {
    this.t += dt;
    this.meanSpeed = damp(this.meanSpeed, targetSpeed, 0.06, dt);
    this.meanFrom = dampAngle(this.meanFrom, targetFrom, 0.04, dt);
    this.gustiness = damp(this.gustiness, gustiness, 0.1, dt);
    const g = this.gustiness;
    const t = this.t;
    this.gust = 1 + g * (0.55 * fbm1(t * 0.07, 11) + 0.3 * noise1(t * 0.55, 17) + 0.15 * noise1(t * 2.1, 23));
    this.speed = Math.max(0, this.meanSpeed * this.gust);
    let from = this.meanFrom + g * 0.22 * fbm1(t * 0.04, 31);
    from %= TAU;
    if (from < 0) from += TAU;
    this.fromBearing = from;
    this.updateVector();
  }

  private updateVector(): void {
    // "From" bearing b points at (sin b, -cos b); the wind blows the other way.
    this.dirX = -Math.sin(this.fromBearing);
    this.dirZ = Math.cos(this.fromBearing);
  }

  /** Logarithmic boundary-layer profile: speed multiplier at height h. */
  static heightFactor(h: number): number {
    return Math.log(Math.max(h, 0.3) / Z0) / LOG_REF;
  }
}
